/**
 * BarueriFlash - Notification Toggle Component
 * UI component to enable/disable push notifications
 */
import React from 'react';
import { Bell, BellOff, Check, X, Loader2 } from 'lucide-react';
import { useNotifications } from '../../hooks/useNotifications';

const NotificationToggle = ({ className = '' }) => {
  const {
    isSupported,
    permission,
    isSubscribed,
    loading,
    error,
    enableNotifications,
    disableNotifications
  } = useNotifications();

  if (!isSupported) {
    return null; // Don't show anything if not supported
  }

  const handleToggle = async () => {
    if (isSubscribed) {
      await disableNotifications();
    } else {
      await enableNotifications();
    }
  };

  const getButtonContent = () => {
    if (loading) {
      return (
        <>
          <Loader2 className="w-4 h-4 animate-spin" />
          <span className="hidden sm:inline ml-2">Carregando...</span>
        </>
      );
    }

    if (permission === 'denied') {
      return (
        <>
          <BellOff className="w-4 h-4 text-red-500" />
          <span className="hidden sm:inline ml-2 text-red-500">Bloqueado</span>
        </>
      );
    }

    if (isSubscribed) {
      return (
        <>
          <Bell className="w-4 h-4 text-green-500" />
          <span className="hidden sm:inline ml-2 text-green-500">Ativado</span>
        </>
      );
    }

    return (
      <>
        <BellOff className="w-4 h-4" />
        <span className="hidden sm:inline ml-2">Ativar Notificações</span>
      </>
    );
  };

  return (
    <div className={`relative ${className}`}>
      <button
        onClick={handleToggle}
        disabled={loading || permission === 'denied'}
        className={`
          flex items-center px-3 py-2 rounded-lg transition-all duration-200
          ${isSubscribed 
            ? 'bg-green-100 text-green-700 hover:bg-green-200' 
            : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
          }
          ${permission === 'denied' ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
          disabled:opacity-50
        `}
        title={
          permission === 'denied' 
            ? 'Notificações bloqueadas. Altere nas configurações do navegador.' 
            : isSubscribed 
              ? 'Clique para desativar notificações' 
              : 'Clique para ativar notificações'
        }
      >
        {getButtonContent()}
      </button>
      
      {error && (
        <div className="absolute top-full left-0 mt-2 p-2 bg-red-100 text-red-700 text-xs rounded-lg shadow-lg whitespace-nowrap">
          {error}
        </div>
      )}
    </div>
  );
};

// Compact version for mobile
export const NotificationToggleCompact = ({ className = '' }) => {
  const { isSupported, isSubscribed, loading, enableNotifications, disableNotifications, permission } = useNotifications();

  if (!isSupported) return null;

  const handleToggle = async () => {
    if (isSubscribed) {
      await disableNotifications();
    } else {
      await enableNotifications();
    }
  };

  return (
    <button
      onClick={handleToggle}
      disabled={loading || permission === 'denied'}
      className={`
        p-2 rounded-full transition-all duration-200
        ${isSubscribed ? 'text-green-500' : 'text-gray-500'}
        hover:bg-gray-100 disabled:opacity-50
        ${className}
      `}
      title={isSubscribed ? 'Notificações ativas' : 'Ativar notificações'}
    >
      {loading ? (
        <Loader2 className="w-5 h-5 animate-spin" />
      ) : isSubscribed ? (
        <Bell className="w-5 h-5" />
      ) : (
        <BellOff className="w-5 h-5" />
      )}
    </button>
  );
};

// Full settings panel
export const NotificationSettings = () => {
  const {
    isSupported,
    permission,
    isSubscribed,
    loading,
    error,
    enableNotifications,
    disableNotifications
  } = useNotifications();

  if (!isSupported) {
    return (
      <div className="p-4 bg-yellow-50 rounded-lg border border-yellow-200">
        <div className="flex items-center gap-2 text-yellow-700">
          <X className="w-5 h-5" />
          <span>Notificações push não são suportadas neste navegador.</span>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 bg-white rounded-xl shadow-sm border">
      <h3 className="font-semibold text-lg mb-4 flex items-center gap-2">
        <Bell className="w-5 h-5" />
        Notificações Push
      </h3>

      {permission === 'denied' && (
        <div className="mb-4 p-3 bg-red-50 rounded-lg border border-red-200">
          <p className="text-red-700 text-sm">
            <strong>Notificações bloqueadas.</strong><br />
            Para ativar, clique no ícone de cadeado na barra de endereço e permita notificações.
          </p>
        </div>
      )}

      {error && (
        <div className="mb-4 p-3 bg-red-50 rounded-lg border border-red-200">
          <p className="text-red-700 text-sm">{error}</p>
        </div>
      )}

      <div className="flex items-center justify-between">
        <div>
          <p className="font-medium">Receber notificações</p>
          <p className="text-sm text-gray-500">
            {isSubscribed 
              ? 'Você receberá alertas sobre pedidos e entregas'
              : 'Ative para receber atualizações em tempo real'
            }
          </p>
        </div>

        <button
          onClick={isSubscribed ? disableNotifications : enableNotifications}
          disabled={loading || permission === 'denied'}
          className={`
            relative w-14 h-7 rounded-full transition-colors duration-200
            ${isSubscribed ? 'bg-[#DC143C]' : 'bg-gray-300'}
            ${loading || permission === 'denied' ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
          `}
        >
          <span
            className={`
              absolute top-1 w-5 h-5 bg-white rounded-full shadow transition-transform duration-200
              ${isSubscribed ? 'translate-x-8' : 'translate-x-1'}
            `}
          >
            {loading && (
              <Loader2 className="w-3 h-3 m-1 animate-spin text-gray-400" />
            )}
          </span>
        </button>
      </div>

      {isSubscribed && (
        <div className="mt-4 pt-4 border-t">
          <p className="text-sm text-gray-600 mb-2">Você receberá notificações sobre:</p>
          <ul className="text-sm text-gray-500 space-y-1">
            <li className="flex items-center gap-2">
              <Check className="w-4 h-4 text-green-500" />
              Atualizações de status do pedido
            </li>
            <li className="flex items-center gap-2">
              <Check className="w-4 h-4 text-green-500" />
              Novas entregas disponíveis (entregadores)
            </li>
            <li className="flex items-center gap-2">
              <Check className="w-4 h-4 text-green-500" />
              Novos pedidos recebidos (lojistas)
            </li>
            <li className="flex items-center gap-2">
              <Check className="w-4 h-4 text-green-500" />
              Confirmação de pagamentos
            </li>
          </ul>
        </div>
      )}
    </div>
  );
};

export default NotificationToggle;
